/**
 * Pure state helpers for a Tryout "run" (Durchlauf).
 *
 * A run binds one empty generation (A/B) + up to MAX_REFINES reprompts + one
 * staging to a single token use. The token is consumed on the FIRST real AI
 * call only: uploads pay at empty-gen; presets are free until a live reprompt
 * or a live staging (missing baked combo) forces real work.
 */

export type TryoutPhase = 'idle' | 'empty' | 'stage' | 'done';
export type EmptyVariant = 'A' | 'B';

export interface TryoutRunState {
  runId: string | null;
  tokenConsumed: boolean;
  phase: TryoutPhase;
  selectedVariant: EmptyVariant | null;
  refineCount: number;
}

export const MAX_REFINES = 2;

export function initialRunState(): TryoutRunState {
  return {
    runId: null,
    tokenConsumed: false,
    phase: 'idle',
    selectedVariant: null,
    refineCount: 0,
  };
}

export function newRunId(): string {
  return crypto.randomUUID();
}

/**
 * Whether this call is the first real AI call of the run and therefore
 * consumes one token use. Pre-baked preset previews ('preset' + 'empty')
 * never consume; anything that actually invokes the model does, unless the
 * run already paid.
 */
export function callConsumesToken(
  state: TryoutRunState,
  source: 'upload' | 'preset',
  action: 'empty' | 'refine' | 'stage',
): boolean {
  if (state.tokenConsumed) return false;
  if (source === 'preset' && action === 'empty') return false; // pre-baked
  return true;
}

export function canRefine(state: TryoutRunState): boolean {
  return state.refineCount < MAX_REFINES;
}
